import { Outlet } from "react-router";

import { UnsavedChangesProvider } from "@/hooks/useUnsavedChanges";

import { ShellProvider } from "./ShellProvider";
import { Sidebar } from "./Sidebar";
import { TaskBar } from "./TaskBar";
import { TopBar } from "./TopBar";

/**
 * Proxmox-style application shell: permanent left sidebar (sections + resource
 * tree), light topbar, light scrollable content, and a light global task
 * panel pinned to the bottom.
 *
 * Layout note: only the <main> scrolls. ``min-h-0`` on every intermediate flex
 * container is what lets ``overflow-auto`` kick in instead of pushing the
 * bottom panel off-screen (the classic flexbox overflow trap).
 *
 * ``UnsavedChangesProvider`` holds every way out of a form that has unsaved changes (see
 * ``hooks/useUnsavedChanges``); ``scroll-pb-24`` keeps a field that takes the keyboard focus out from
 * under the sticky action bar of a form (``FormActionBar``, about 60 px tall): the browser scrolls it
 * into view above that margin instead of behind the bar.
 */
export function AppShell() {
  return (
    <ShellProvider>
      <UnsavedChangesProvider>
        <div className="flex h-screen overflow-hidden bg-sunken text-ink">
          <Sidebar />
          <div className="flex min-w-0 flex-1 flex-col">
            <TopBar />
            <div className="flex min-h-0 flex-1 flex-col">
              <main className="min-h-0 flex-1 scroll-pb-24 overflow-auto bg-ground text-ink">
                {/* The console's column (1152 px): the width of a "list" page; a "form" page
                    narrows it to 720 (see ``Page``). */}
                <div className="mx-auto w-full max-w-page px-6 py-8">
                  <Outlet />
                </div>
              </main>
              <TaskBar />
            </div>
          </div>
        </div>
      </UnsavedChangesProvider>
    </ShellProvider>
  );
}
